/**
 * Network status monitoring and information utilities.
 * Detects online/offline status and retrieves connection information.
 */

'use strict';

import type { DotNetObjectReference, NetworkInfo } from '../types.js';
import { safeInvokeSync } from '../utils/dotnet.js';

/**
 * Internal state for network functionality.
 */
interface NetworkState {
    dotnet: DotNetObjectReference | null;
    onlineHandler: (() => void) | null;
    offlineHandler: (() => void) | null;
}

const state: NetworkState = {
    dotnet: null,
    onlineHandler: null,
    offlineHandler: null
};

/**
 * Event listener options for passive events (better scroll performance).
 */
const PASSIVE_LISTENER_OPTIONS: AddEventListenerOptions = { passive: true };

/**
 * Add event listener with passive option, falling back to standard if not supported.
 */
function addPassiveListener(
    target: Window,
    event: string,
    handler: () => void
): void {
    try {
        target.addEventListener(event, handler, PASSIVE_LISTENER_OPTIONS);
    } catch {
        // Fallback for browsers that don't support options
        target.addEventListener(event, handler);
    }
}

/**
 * Initialize network online/offline listeners.
 * 
 * @param dotnetHelper - .NET object reference for callbacks
 * @returns Current online status
 */
export function initialize(dotnetHelper: DotNetObjectReference): boolean {
    try {
        // Remove prior listeners if any
        cleanupListeners();

        state.dotnet = dotnetHelper;
        state.onlineHandler = () => {
            safeInvokeSync(dotnetHelper, 'OnOnlineChanged', true);
        };
        state.offlineHandler = () => {
            safeInvokeSync(dotnetHelper, 'OnOnlineChanged', false);
        };

        // online/offline events never call preventDefault -> safe to mark passive
        addPassiveListener(window, 'online', state.onlineHandler);
        addPassiveListener(window, 'offline', state.offlineHandler);

        return navigator.onLine;
    } catch {
        return false;
    }
}

/**
 * Clean up network event listeners.
 */
function cleanupListeners(): void {
    if (state.onlineHandler) {
        try {
            window.removeEventListener('online', state.onlineHandler);
        } catch {
            // Ignore errors during cleanup
        }
    }

    if (state.offlineHandler) {
        try {
            window.removeEventListener('offline', state.offlineHandler);
        } catch {
            // Ignore errors during cleanup
        }
    }
}

/**
 * Dispose network listeners and clear state.
 */
export function dispose(): void {
    try {
        cleanupListeners();
    } finally {
        state.onlineHandler = null;
        state.offlineHandler = null;
        state.dotnet = null;
    }
}

/**
 * Check if the browser is currently online.
 * 
 * @returns True if navigator reports online status
 */
export function isOnline(): boolean {
    try {
        return navigator.onLine;
    } catch {
        return false;
    }
}

/**
 * Get basic network information when available.
 * Uses the Network Information API if supported.
 * 
 * @returns Network information object with connection details
 */
export function getNetworkInfo(): NetworkInfo {
    try {
        const connection: any =
            (navigator as any).connection ||
            (navigator as any).mozConnection ||
            (navigator as any).webkitConnection;

        return {
            effectiveType: connection?.effectiveType ?? null,
            downlink: connection?.downlink ?? null,
            saveData: connection?.saveData ?? null
        };
    } catch {
        return {
            effectiveType: null,
            downlink: null,
            saveData: null
        };
    }
}
